import type { ElementType, MouseEventHandler, ReactNode } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import type { SxProps, Theme } from "@mui/material/styles";
import { PrimeGlyph } from "./PrimeGlyph";
import type { PrimeGlyphName } from "./prime-glyphs";

// A PRIMENG `p-button`, SIZED AND COLORED AS THE PILLARS THEMES DRAW IT (AT THE 14PX
// ROOT): 0.5REM 0.75REM PADDING, 1REM TEXT, 4PX RADIUS, SO 2.357REM TALL; ICON-ONLY IS A
// 2.357REM SQUARE (A CIRCLE WHEN ROUNDED); `sm` / `lg` ARE THE 0.875REM / 1.25REM SIZES.
// FILLED USES THE SEVERITY COLOR; OUTLINED AND TEXT USE `#007bff` FOR PRIMARY (THE
// THEME'S OWN VALUE, NOT PILLARS BLUE), TRANSPARENT AT REST WITH A 4% TINT ON HOVER AND 16%
// WHILE PRESSED. THE LABEL IS `flex: 1 1 auto` AND CENTERED, SO THE ICON STAYS AT THE LEFT
// EDGE OF A WIDE BUTTON (`min-width-100`) AND THE LABEL CENTERS IN WHAT IS LEFT.
export type Severity = "primary" | "secondary" | "success" | "danger" | "warning" | "info";

const FILL: Record<Severity, string> = {
  primary: "var(--mui-palette-primary-main)",
  secondary: "var(--mui-palette-secondary-main)",
  success: "var(--mui-palette-success-main)",
  danger: "var(--mui-palette-error-main)",
  warning: "var(--mui-palette-warning-main)",
  info: "var(--mui-palette-info-main)",
};
const ON_FILL: Record<Severity, string> = {
  primary: "#ffffff",
  secondary: "#ffffff",
  success: "var(--mui-palette-success-contrastText)",
  danger: "var(--mui-palette-error-contrastText)",
  warning: "var(--mui-palette-warning-contrastText)",
  info: "var(--mui-palette-info-contrastText)",
};
const HOVER: Record<Severity, string> = {
  primary: "var(--mui-palette-primary-dark)",
  secondary: "#5a6268",
  success: "#218838",
  danger: "#c82333",
  warning: "#e0a800",
  info: "#138496",
};
// OUTLINED / TEXT FOREGROUND
const LINE: Record<Severity, string> = { ...FILL, primary: "#007bff" };
const TINT: Record<Severity, [string, string]> = {
  primary: ["rgba(0, 123, 255, 0.04)", "rgba(0, 123, 255, 0.16)"],
  secondary: ["rgba(108, 117, 125, 0.04)", "rgba(108, 117, 125, 0.16)"],
  success: ["rgba(40, 167, 69, 0.04)", "rgba(40, 167, 69, 0.16)"],
  danger: ["rgba(220, 53, 69, 0.04)", "rgba(220, 53, 69, 0.16)"],
  warning: ["rgba(255, 193, 7, 0.04)", "rgba(255, 193, 7, 0.16)"],
  info: ["rgba(23, 162, 184, 0.04)", "rgba(23, 162, 184, 0.16)"],
};

export interface PButtonProps {
  label?: ReactNode;
  // `[badge]`: A WHITE 1REM PILL WITH `#007bff` TEXT, 0.5REM AFTER THE LABEL (BOTH THEMES); A
  // ONE-CHARACTER VALUE IS A CIRCLE (`p-badge-no-gutter`)
  badge?: ReactNode;
  // A PRIMEICONS NAME (THE `pi-*` SUFFIX), OR ANY NODE
  icon?: PrimeGlyphName | ReactNode;
  iconPos?: "left" | "right";
  severity?: Severity;
  variant?: "filled" | "outlined" | "text";
  rounded?: boolean;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  disabled?: boolean;
  // TRUE WHERE ANGULAR USES THE `<p-button>` COMPONENT RATHER THAN THE `pButton` DIRECTIVE:
  // DISABLED, ITS HOST TAKES `.p-disabled` TOO, SO IT DIMS TWICE (0.65 x 0.65)
  hostElement?: boolean;
  type?: "button" | "submit";
  onClick?: MouseEventHandler<HTMLButtonElement>;
  ariaLabel?: string;
  ariaPressed?: boolean;
  component?: ElementType;
  to?: string;
  sx?: SxProps<Theme>;
  className?: string;
}

const SIZES = {
  sm: { fontSize: "0.875rem", padding: "0.4375rem 0.65625rem", square: "2.357rem" },
  md: { fontSize: "1rem", padding: "0.5rem 0.75rem", square: "2.357rem" },
  lg: { fontSize: "1.25rem", padding: "0.625rem 0.9375rem", square: "2.357rem" },
};

export function PButton(props: PButtonProps) {
  const {
    label,
    badge,
    icon,
    iconPos = "left",
    severity = "primary",
    variant = "filled",
    rounded = false,
    size = "md",
    loading = false,
    disabled = false,
    hostElement = false,
    type = "button",
    onClick,
    ariaLabel,
    ariaPressed,
    component,
    to,
    sx,
    className,
  } = props;
  const s = SIZES[size];
  const iconOnly = label === undefined || label === null || label === "";
  // PRIMEICONS ARE 1EM OF THE BUTTON'S FONT SIZE (14PX AT MD); `_overrides.scss` GROWS THE
  // `lg` ICON TO 1.5REM AND NUDGES IT WITH 2PX BOTTOM / 1PX LEFT PADDING
  const glyph =
    typeof icon === "string" ? (
      size === "lg" ? (
        <span style={{ display: "flex", paddingBottom: 2, paddingLeft: 1 }}>
          <PrimeGlyph name={icon as PrimeGlyphName} size="1.5rem" />
        </span>
      ) : (
        <PrimeGlyph name={icon as PrimeGlyphName} size="1em" />
      )
    ) : (
      icon
    );
  const iconNode = loading ? <CircularProgress size="1em" color="inherit" /> : glyph;

  const filled = variant === "filled";
  return (
    <ButtonBase
      type={component ? undefined : type}
      component={component ?? "button"}
      {...(to ? { to } : {})}
      onClick={onClick}
      disabled={disabled || loading}
      aria-label={ariaLabel ?? (typeof label === "string" ? undefined : undefined)}
      aria-pressed={ariaPressed}
      disableRipple
      className={className}
      sx={[
        {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: iconOnly ? 0 : "0.5rem",
          flexDirection: iconPos === "right" ? "row-reverse" : "row",
          fontFamily: "inherit",
          fontSize: s.fontSize,
          lineHeight: "normal",
          // ICON-ONLY IS 2.357REM WIDE WITH 0.5REM VERTICAL PADDING (28PX TALL), OR A 2.357REM
          // CIRCLE WHEN ROUNDED; LABELLED BUTTONS TAKE THEIR NATURAL HEIGHT
          padding: iconOnly ? (rounded ? 0 : "0.5rem 0") : s.padding,
          width: iconOnly ? s.square : undefined,
          height: iconOnly && rounded ? s.square : undefined,
          borderRadius: rounded ? (iconOnly ? "50%" : "2rem") : "4px",
          // `_overrides.scss`: `.p-button { border: 0 !important }`
          border: 0,
          transition: "background-color 0.15s, border-color 0.15s, box-shadow 0.15s",
          // NO `white-space` RULE, AS IN PRIMENG: A LABEL IN A TOO-NARROW COLUMN WRAPS ("3 / MONTHS")
          userSelect: "none",
          ...(filled
            ? {
                color: ON_FILL[severity],
                backgroundColor: FILL[severity],
                "&:hover": { backgroundColor: HOVER[severity] },
              }
            : {
                color: LINE[severity],
                backgroundColor: "transparent",
                // `.p-button { border: 0 !important }` TAKES THE OUTLINE OFF OUTLINED BUTTONS TOO, SO
                // IN ANGULAR THEY DRAW AS TEXT BUTTONS; SO DO THESE
                boxShadow: "none",
                "&:hover": { backgroundColor: TINT[severity][0] },
                "&:active": { backgroundColor: TINT[severity][1] },
              }),
          // `_overrides.scss` REMOVES FOCUS RINGS APP-WIDE (`*:focus`)
          "&.Mui-disabled": { opacity: hostElement ? 0.65 * 0.65 : 0.65 },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {iconNode}
      {!iconOnly && <span style={{ flex: "1 1 auto", textAlign: "center" }}>{label}</span>}
      {badge !== undefined && badge !== null && (
        <span
          style={{
            marginLeft: iconOnly ? "0.5rem" : 0,
            minWidth: "1rem",
            height: "1rem",
            lineHeight: "1rem",
            padding: String(badge).length === 1 ? 0 : "0 0.5rem",
            borderRadius: String(badge).length === 1 ? "50%" : "10px",
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "#007bff",
            backgroundColor: "#ffffff",
            textAlign: "center",
          }}
        >
          {badge}
        </span>
      )}
    </ButtonBase>
  );
}
